import type { ChatAutoSelection, ChatResourceCandidate } from "../services/chatService";

/** Drop scored resources below this share from composer chips. */
export const CANDIDATE_DISPLAY_MIN_PROBABILITY = 0.95;
/** When fewer than 3 resources are scored, auto-pick first at/above this share. */
export const SPARSE_AUTO_SELECT_MIN_PROBABILITY = 0.5;
export const SPARSE_SCORED_RESOURCE_LIMIT = 3;

export type ResourceMeta = {
  name: string;
  probability?: number;
  kind?: string;
};

/** Strip tenant-prefixed physical index ids when no guide title is available. */
export function friendlyIndexLabel(id: string): string {
  const trimmed = (id || "").trim();
  if (!trimmed) return trimmed;
  const m = trimmed.match(/^ws-[^-]+-(.+)$/i);
  return m?.[1] || trimmed;
}

export function formatProbabilityPct(probability?: number): string | undefined {
  if (probability == null || Number.isNaN(probability)) return undefined;
  const pct = probability <= 1 ? Math.round(probability * 100) : Math.round(probability);
  if (pct <= 0) return undefined;
  return `${pct}%`;
}

/** Truncate display name to maxChars and append "..." when longer. */
export function truncateResourceName(name: string, maxChars = 6): string {
  const trimmed = (name || "").trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, maxChars)}...`;
}

export function resourceChipLabel(
  id: string,
  meta?: ResourceMeta,
  options?: { truncateNameTo?: number }
): string {
  const name = meta?.name?.trim() || friendlyIndexLabel(id);
  const displayName =
    options?.truncateNameTo != null ? truncateResourceName(name, options.truncateNameTo) : name;
  const pct = formatProbabilityPct(meta?.probability);
  return pct ? `${displayName} · ${pct}` : displayName;
}

/** Full untruncated chip text for tooltips. */
export function resourceChipFullLabel(id: string, meta?: ResourceMeta): string {
  return resourceChipLabel(id, meta);
}

export function mergeResourceMetaFromSelection(
  selection: ChatAutoSelection | undefined | null,
  prev: Record<string, ResourceMeta> = {}
): Record<string, ResourceMeta> {
  const next: Record<string, ResourceMeta> = { ...prev };
  if (!selection) return next;

  const put = (id: string | undefined, name?: string, probability?: number, kind?: string) => {
    if (!id) return;
    const existing = next[id];
    next[id] = {
      name: (name && name.trim()) || existing?.name || friendlyIndexLabel(id),
      probability: probability ?? existing?.probability,
      kind: kind || existing?.kind,
    };
  };

  for (const d of selection.indexDetails || []) {
    put(d?.id, d?.name, d?.probability, "index");
  }
  for (const idx of selection.indices || []) {
    put(idx, undefined, undefined, "index");
  }
  for (const c of selection.openApiCatalogs || []) {
    put(c?.catalogId, c?.name, undefined, "openapi");
  }
  for (const a of selection.adaptors || []) {
    put(a?.adaptorId, a?.name, a?.probability, "adaptor");
  }
  for (const c of selection.candidates || []) {
    put(c?.id, c?.name, c?.probability, c?.kind);
  }
  return next;
}

/**
 * Apply probability gates for composer chips:
 * - hide below 95%
 * - if fewer than 3 scored, keep only the first at or above 50% when nothing reaches 95%
 */
export function filterCandidatesByProbability(
  candidates: ChatResourceCandidate[] | undefined | null
): ChatResourceCandidate[] {
  const list = (candidates || []).filter((c) => Boolean(c?.id));
  if (list.length === 0) return [];
  const high = list.filter((c) => (c.probability ?? 0) >= CANDIDATE_DISPLAY_MIN_PROBABILITY);
  if (high.length > 0) return high;
  if (list.length < SPARSE_SCORED_RESOURCE_LIMIT) {
    const sparse = list.find((c) => (c.probability ?? 0) >= SPARSE_AUTO_SELECT_MIN_PROBABILITY);
    return sparse ? [sparse] : [];
  }
  return [];
}

export function rankedCandidatesForDisplay(
  selection: ChatAutoSelection | undefined | null,
  limit = 5
): ChatResourceCandidate[] {
  const list = Array.isArray(selection?.candidates) ? selection!.candidates! : [];
  return filterCandidatesByProbability(list)
    .slice(0, limit)
    .map((c) => ({
      ...c,
      name: c.name?.trim() || friendlyIndexLabel(c.id || ""),
    }));
}
